import React, { useState } from "react";
import { Stack, Typography, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SkillLevelBadge from "./SkillLevelBadge";
import UsedWorkHistoriesModal from "./UsedWorkHistoriesModal";
import { FONT_SIZE } from "../../../constants/fontSizes";
import { TEXT_COLOR_PRIMARY } from "../../../constants/colors";
import { editSkillSheetPathFor } from "../../utils/skillSheetRouting";

/**
 * フレームワーク / ライブラリ 1件分の表示コンポーネント
 *
 * 表示内容：
 * - スキルレベルバッジ（小サイズ）
 * - フレームワーク / ライブラリ名
 * - 経験年数（Rails 側で整形済みの表示用ラベル）
 */
const SkillFrameworkItem = ({ framework, owner, showEditButton }) => {
  const navigate = useNavigate();

  // この FW / ライブラリを使用した work_history が存在するかどうか
  const hasUsedWorkHistories =
    framework?.used_work_histories && framework.used_work_histories.length > 0;

  // 使用した work_history 一覧モーダルの表示／非表示
  const [openModal, setOpenModal] = useState(false);

  // FW 名クリック時にモーダルを開く
  const handleFrameworkClick = () => {
    if (!hasUsedWorkHistories) return;
    setOpenModal(true);
  };

  const handleClose = () => setOpenModal(false);

  // 選択した work_history の編集画面へ遷移する
  const handleWorkHistoryClick = (workHistoryId) => {
    setOpenModal(false);
    navigate(editSkillSheetPathFor(workHistoryId, owner));
  };

  return (
    <Stack alignItems="center">
      {/* レベルバッジ（FW 用の小サイズ） */}
      <SkillLevelBadge level={framework.level} />

      {/* フレームワーク / ライブラリ名 */}
      <Link
        component="button"
        type="button"
        onClick={handleFrameworkClick}
        disabled={!hasUsedWorkHistories}
        underline="none"
        sx={{
          maxWidth: "100%",
          textAlign: "center",
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: 1.2,
          fontSize: FONT_SIZE.SM,
          fontWeight: "bold",

          color: "inherit",
          p: 0,
          background: "none",
          border: "none",

          cursor: hasUsedWorkHistories ? "pointer" : "default",

          "&:hover": {
            textDecoration: "underline",
            textDecorationColor: "#000",
          },
        }}
        aria-haspopup="dialog"
        aria-expanded={openModal ? "true" : "false"}
      >
        {framework?.displayName}
      </Link>

      {/* 経験年数（例：1年6ヶ月） */}
      <Typography
        whiteSpace="nowrap"
        sx={{ fontSize: FONT_SIZE.XS, color: TEXT_COLOR_PRIMARY }}
      >
        {framework?.experienceLabel}
      </Typography>

      {/* モーダル（共通コンポーネント） */}
      <UsedWorkHistoriesModal
        open={openModal}
        onClose={handleClose}
        title={`${framework?.displayName} を使用したプロジェクト`}
        workHistories={framework.used_work_histories}
        onClickWorkHistory={handleWorkHistoryClick}
        showEditButton={showEditButton}
      />
    </Stack>
  );
};

export default SkillFrameworkItem;
